import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

/* ======================= Helpers ======================= */

const randInt = (min, max) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

// parsowanie dziesiętne (kropka / przecinek)
const parseDecimalFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;
  const normalized = trimmed.replace(",", ".");
  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  return value;
};

// ułamek zwykły / mieszany
const simplifyFraction = (num, den) => {
  const gcd = (a, b) => {
    a = Math.abs(a);
    b = Math.abs(b);
    if (a === 0 && b === 0) return 1;
    while (b !== 0) {
      const t = b;
      b = a % b;
      a = t;
    }
    return a === 0 ? 1 : a;
  };
  const g = gcd(num, den);
  return { numerator: num / g, denominator: den / g };
};

const parseFractionFromString = (input) => {
  if (typeof input !== "string") return null;
  const trimmed = input.trim();
  if (!trimmed) return null;

  // jeżeli pojawia się przecinek/kropka, traktujemy jako liczbę dziesiętną
  if (trimmed.includes(".") || trimmed.includes(",")) return null;

  let sign = 1;
  let rest = trimmed;
  if (rest[0] === "-" || rest[0] === "+") {
    if (rest[0] === "-") sign = -1;
    rest = rest.slice(1).trim();
  }

  let whole = 0;
  let num;
  let den;

  const spaceParts = rest.split(/\s+/);

  if (spaceParts.length === 2) {
    // zapis mieszany: "2 3/5"
    whole = parseInt(spaceParts[0], 10);
    if (!Number.isFinite(whole)) return null;

    const fracParts = spaceParts[1].split("/");
    if (fracParts.length !== 2) return null;

    num = parseInt(fracParts[0], 10);
    den = parseInt(fracParts[1], 10);
    if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;

    num = sign * (whole * den + num);
  } else {
    // "3/5" albo "4"
    const fracParts = rest.split("/");
    if (fracParts.length === 1) {
      num = sign * parseInt(fracParts[0], 10);
      den = 1;
      if (!Number.isFinite(num)) return null;
    } else if (fracParts.length === 2) {
      num = sign * parseInt(fracParts[0], 10);
      den = parseInt(fracParts[1], 10);
      if (!Number.isFinite(num) || !Number.isFinite(den) || den === 0) return null;
    } else return null;
  }

  return simplifyFraction(num, den);
};

// format do 2 miejsc po przecinku bez nieznaczących zer
const formatMax2 = (value) => {
  let s = value.toFixed(2);
  s = s.replace(/\.?0+$/, "");
  return s;
};

/* ======================= GENERATOR ======================= */
/**
 * Zadanie: porównanie cen dwóch towarów – o ile procent druga
 * cena jest większa lub mniejsza od pierwszej.
 *
 * Teraz mamy trzy „scenki”:
 *  - para skarpet (ok. 10–30 zł),
 *  - koszulka (ok. 40–120 zł),
 *  - para butów (ok. 400–600 zł),
 * z lekkim dociążeniem koszulki.
 */

const gcd = (a, b) => {
  a = Math.abs(a);
  b = Math.abs(b);
  if (a === 0 && b === 0) return 1;
  while (b !== 0) {
    const t = b;
    b = a % b;
    a = t;
  }
  return a === 0 ? 1 : a;
};

const PRODUCT_TIERS = [
  {
    productLabel: "pary skarpet",
    minPrice: 10,
    maxPrice: 30,
    weight: 1,
  },
  {
    productLabel: "koszulki",
    minPrice: 40,
    maxPrice: 120,
    weight: 2, // trochę częściej
  },
  {
    productLabel: "pary butów",
    minPrice: 400,
    maxPrice: 600,
    weight: 1,
  },
];

const chooseTier = () => {
  const totalWeight = PRODUCT_TIERS.reduce(
    (sum, tier) => sum + (tier.weight || 1),
    0
  );
  let r = Math.random() * totalWeight;
  for (let i = 0; i < PRODUCT_TIERS.length; i += 1) {
    const w = PRODUCT_TIERS[i].weight || 1;
    if (r < w) return PRODUCT_TIERS[i];
    r -= w;
  }
  return PRODUCT_TIERS[0];
};

export function generateTask() {
  const isGreater = Math.random() < 0.5; // true: druga większa, false: mniejsza

  // procent – wielokrotność 5, żeby był ładny
  const percents = [5, 10, 15, 20, 25, 30, 35, 40, 50];
  const percent = percents[randInt(0, percents.length - 1)];

  const tier = chooseTier();

  // chcemy całkowite ceny:
  // price2 = price1 * (100 ± p) / 100
  const factorNum = 100 + (isGreater ? percent : -percent);
  const step = 100 / gcd(100, factorNum);

  // dobieramy price1 tak, żeby wpadł w zakres cenowy danego produktu
  let price1;
  const kMin = Math.ceil(tier.minPrice / step);
  const kMax = Math.floor(tier.maxPrice / step);

  if (kMin <= kMax) {
    const k = randInt(kMin, kMax);
    price1 = k * step;
  } else {
    // awaryjnie – stary sposób, gdyby zakres się nie zgrał
    price1 = step * randInt(3, 10);
  }

  const price2 = (price1 * factorNum) / 100;

  return {
    type: "PercentComparisonRelativeTask",
    data: {
      price1,
      price2,
      percent,
      isGreater,
      productLabel: tier.productLabel,
    },
  };
}

/* ======================= RENDER TASK ======================= */

export function renderTask({ data }) {
  const { price1, price2, isGreater, productLabel } = data;

  const relation = isGreater ? "większa" : "mniejsza";

  return (
    <p>
      Cena {productLabel} w pierwszym sklepie wynosi{" "}
      <InlineMath math={`${price1}\\,\\text{zł}`} />, a w drugim sklepie{" "}
      <InlineMath math={`${price2}\\,\\text{zł}`} />. O ile procent cena w
      drugim sklepie jest {relation} od ceny w pierwszym sklepie?
    </p>
  );
}

/* ======================= INPUT ======================= */

export function renderInput(_, userAnswer, onChange, disabled) {
  return (
    <div style={{ marginTop: "0.75rem" }}>
      <FractionInput
        value={userAnswer}
        onChange={onChange}
        disabled={disabled}
        placeholder="np. 25, 12.5, 12 1/2"
        label=""
        suffixLatex="\\%"
      />
    </div>
  );
}

/* ======================= CHECK ======================= */

export function checkAnswer({ data }, userAnswer) {
  const expected = data.percent;

  if (!userAnswer || !userAnswer.raw) {
    return { isCorrect: false, expected, actual: null, comment: null };
  }

  const raw = String(userAnswer.raw).trim();

  // 1) dziesiętna
  let value = parseDecimalFromString(raw);

  // 2) jeżeli nie – ułamek
  if (value === null) {
    const frac = parseFractionFromString(raw);
    if (frac && frac.denominator !== 0) {
      value = frac.numerator / frac.denominator;
    }
  }

  if (value === null) {
    return { isCorrect: false, expected, actual: null, comment: null };
  }

  const isCorrect = Math.abs(value - expected) <= 0.01;

  return { isCorrect, expected, actual: value, comment: null };
}

/* ======================= ANSWER & USER ANSWER ======================= */

export function renderAnswer({ data }) {
  const text = formatMax2(data.percent);
  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={`${text}\\%`} />
    </div>
  );
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || !userAnswer.raw || !String(userAnswer.raw).trim()) {
    return <i>(brak odpowiedzi)</i>;
  }
  const norm = String(userAnswer.raw).trim().replace(",", ".");
  return (
    <div style={{ marginTop: "0.5rem", textAlign: "left" }}>
      <InlineMath math={`${norm}\\%`} />
    </div>
  );
}

/* ======================= EXPLANATION ======================= */

export function renderExplanation({ data }) {
  const { price1, price2, percent } = data;

  const diff = price2 - price1;
  const diffAbs = Math.abs(diff);

  const rel = diffAbs / price1;
  const relStr = formatMax2(rel * 100);
  const diffStr = formatMax2(diffAbs);

  const step1 = `\\Delta = ${price2} - ${price1} = ${diffStr}\\,\\text{zł}`;
  const step2 = `\\dfrac{\\Delta}{${price1}} \\cdot 100\\% = \\dfrac{${diffStr}}{${price1}} \\cdot 100\\% = ${relStr}\\%`;

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: 12,
        marginTop: 12,
      }}
    >
      <p>
        <strong>Krok 1:</strong> Obliczamy różnicę cen.
      </p>
      <BlockMath math={step1} />

      <p>
        <strong>Krok 2:</strong> Odnosimy różnicę do ceny w pierwszym sklepie i
        zamieniamy na procent.
      </p>
      <BlockMath math={step2} />

      <p>
        Otrzymujemy, że cena w drugim sklepie jest większa/mniejsza o{" "}
        <InlineMath math={`${percent}\\%`} />.
      </p>
    </div>
  );
}
